import React, { useEffect, useRef } from "react";
import { TextField, InputAdornment } from "@mui/material";
import { Search } from "@mui/icons-material";
import { Col } from "react-bootstrap";

import { H1, P1, P2, P3 } from "../components/theme/text/Headings";
import {
  White,
  White08,
  White05,
  PageGradient,
  Black,
} from "../components/theme/colors";
import { ProPicSm, SecPicSm } from "../components/theme/ProfilePics";
import { ButtonOutlineSmall } from "../components/theme/Button";
import Layout from "../components/layout/layout";
import {
  SearchContainer,
  FriendsContainer,
  FriendInfo,
  SearchResultCard,
  InfoBox,
  QuickAddContainer,
  QuickAddScroll,
  QuickAddCard,
  FriendArrow,
  RemoveSwipeButton,
  TraitsContainer,
  TraitItem,
  FriendListItem,
  FriendListInfo,
  FriendDetailsColumn,
  FriendNameRow,
} from "../components/friends/FriendStyles";
import { Link } from "react-router-dom";
import { useFriendsData } from "../hooks/friends/useFriendsData";
import { useFriendActions } from "../hooks/friends/useFriendActions";
import { useUserSearch } from "../hooks/friends/useUserSearch";
import { useQuickAddUsers } from "../hooks/friends/useQuickAddUsers";
import {
  SwipeableList,
  SwipeableListItem,
  SwipeAction,
  TrailingActions,
  Type,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";
import { useLoadingManager } from "../services/LoadingManager";

const Friends = () => {
  const {
    userProfile,
    friends,
    incomingRequests,
    outgoingRequests,
    friendsBig3,
    loadFriendsAndRequests,
    invalidateFriendCaches,
  } = useFriendsData();

  const { searchQuery, setSearchQuery, isSearching, searchResults } =
    useUserSearch(userProfile);
  const { quickAddUsers } = useQuickAddUsers(userProfile);
  const {
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    removeFriend,
  } = useFriendActions({
    loadFriendsAndRequests,
    invalidateFriendCaches,
  });

  // real LoadingManager ref to use isLoading() in buttons
  const loadingManager = useLoadingManager();
  const loadingManagerRef = useRef(loadingManager);
  useEffect(() => {
    loadingManagerRef.current = loadingManager;
  }, [loadingManager]);

  const renderBig3Traits = (friendId) => {
    const big3Data = friendsBig3[friendId];
    if (!big3Data) {
      return (
        <P3 color={White08} style={{ fontWeight: 600, margin: 0 }}>
          No traits to show
        </P3>
      );
    }

    const traits = [
      big3Data.personality,
      big3Data.political,
      big3Data.thinking,
    ].filter(Boolean);
    if (traits.length === 0) {
      return (
        <P3 color={White08} style={{ fontWeight: 600, margin: 0 }}>
          No traits to show
        </P3>
      );
    }

    return (
      <TraitsContainer>
        {traits.map((trait, index) => {
          const IconComponent = trait.icon;
          return (
            <TraitItem key={index}>
              <P3 color={White08} style={{ fontWeight: 600, margin: 0 }}>
                {trait.label}
              </P3>
              <IconComponent style={{ fontSize: 16, color: White08 }} />
            </TraitItem>
          );
        })}
      </TraitsContainer>
    );
  };

  const trailingActions = (friendId) => (
    <TrailingActions>
      <SwipeAction destructive={true} onClick={() => removeFriend(friendId)}>
        <RemoveSwipeButton>Remove</RemoveSwipeButton>
      </SwipeAction>
    </TrailingActions>
  );

  return (
    <Layout $background={PageGradient} $navbarBackground={Black}>
      <Col
        lg={{ offset: 4, span: 4 }}
        md={{ offset: 3, span: 6 }}
        sm={{ offset: 2, span: 8 }}
        xs={{ span: 12 }}
      >
        {/* Search bar */}
        <SearchContainer>
          <TextField
            fullWidth
            placeholder="Search by username"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: White08 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "16px",
                color: White,
                padding: "12px 8px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                "& fieldset": {
                  borderColor: White05,
                },
                "&:hover fieldset": {
                  borderColor: White08,
                },
                "&.Mui-focused fieldset": {
                  borderColor: White08,
                },
              },
            }}
          />
        </SearchContainer>

        {/* Search Results */}
        {searchQuery && (
          <div className="mt-15">
            {isSearching ? (
              <InfoBox>
                <P3 color={White08}>Searching...</P3>
              </InfoBox>
            ) : searchResults.length > 0 ? (
              <FriendsContainer>
                {searchResults.map((user) => {
                  const status = user.relationshipStatus || {};
                  const isFriend = status.isFriend;
                  const hasOutgoingRequest = status.hasOutgoingRequest;
                  const hasIncomingRequest = status.hasIncomingRequest;

                  return (
                    <SearchResultCard key={user.id}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {user.photoURL ? (
                          <ProPicSm src={user.photoURL} />
                        ) : (
                          <SecPicSm>
                            <H1>
                              {user.displayName ? user.displayName[0] : "?"}
                            </H1>
                          </SecPicSm>
                        )}
                        <FriendInfo>
                          <P1 color={White}>{user.displayName || "No name"}</P1>
                          <P3 color={White08}>
                            @{user.username.toLowerCase()}
                          </P3>
                        </FriendInfo>
                      </div>

                      {/* Show different buttons based on relationship status */}
                      {isFriend ? (
                        <Link
                          to={`/${user.username.toLowerCase()}`}
                          style={{ textDecoration: "none" }}
                        >
                          <ButtonOutlineSmall $background={White08}>
                            View
                          </ButtonOutlineSmall>
                        </Link>
                      ) : hasIncomingRequest ? (
                        <div style={{ display: "flex", gap: "8px" }}>
                          <ButtonOutlineSmall
                            $background={White08}
                            onClick={() => acceptFriendRequest(user.id)}
                            disabled={loadingManagerRef.current.isLoading(
                              `accept-request-${user.id}`
                            )}
                          >
                            {loadingManagerRef.current.isLoading(
                              `accept-request-${user.id}`
                            )
                              ? "..."
                              : "Accept"}
                          </ButtonOutlineSmall>
                          <ButtonOutlineSmall
                            $background={White08}
                            onClick={() => declineFriendRequest(user.id)}
                            disabled={loadingManagerRef.current.isLoading(
                              `decline-request-${user.id}`
                            )}
                          >
                            {loadingManagerRef.current.isLoading(
                              `decline-request-${user.id}`
                            )
                              ? "..."
                              : "Decline"}
                          </ButtonOutlineSmall>
                        </div>
                      ) : hasOutgoingRequest ? (
                        <ButtonOutlineSmall
                          $background={White08}
                          onClick={() => cancelFriendRequest(user.id)}
                          disabled={loadingManagerRef.current.isLoading(
                            `cancel-request-${user.id}`
                          )}
                        >
                          {loadingManagerRef.current.isLoading(
                            `cancel-request-${user.id}`
                          )
                            ? "Canceling..."
                            : "Pending"}
                        </ButtonOutlineSmall>
                      ) : (
                        <ButtonOutlineSmall
                          $background={White08}
                          onClick={() => sendFriendRequest(user.id)}
                          disabled={loadingManagerRef.current.isLoading(
                            `add-friend-${user.id}`
                          )}
                        >
                          {loadingManagerRef.current.isLoading(
                            `add-friend-${user.id}`
                          )
                            ? "Adding..."
                            : "Add"}
                        </ButtonOutlineSmall>
                      )}
                    </SearchResultCard>
                  );
                })}
              </FriendsContainer>
            ) : (
              <InfoBox>
                <P3 color={White08}>
                  No users found with username "{searchQuery}"
                </P3>
              </InfoBox>
            )}
          </div>
        )}

        {/* Incoming Friend Requests */}
        {incomingRequests.length > 0 && !searchQuery && (
          <div className="mt-20">
            <P2 bold color={White08} style={{ padding: "20px 20px 10px 20px" }}>
              Friend Requests
            </P2>
            <FriendsContainer>
              {incomingRequests.map((requester) => (
                <SearchResultCard key={requester.id}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {requester.photoURL ? (
                      <ProPicSm src={requester.photoURL} />
                    ) : (
                      <SecPicSm>
                        <H1>
                          {requester.displayName
                            ? requester.displayName[0]
                            : "?"}
                        </H1>
                      </SecPicSm>
                    )}
                    <FriendInfo>
                      <P1 color={White}>
                        {requester.displayName || "No name"}
                      </P1>
                      <P3 color={White08}>
                        @{requester.username.toLowerCase()}
                      </P3>
                    </FriendInfo>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <ButtonOutlineSmall
                      $background={White}
                      onClick={() => acceptFriendRequest(requester.id)}
                      disabled={loadingManagerRef.current.isLoading(
                        `accept-request-${requester.id}`
                      )}
                    >
                      {loadingManagerRef.current.isLoading(
                        `accept-request-${requester.id}`
                      )
                        ? "..."
                        : "Accept"}
                    </ButtonOutlineSmall>
                    <ButtonOutlineSmall
                      $background={White05}
                      onClick={() => declineFriendRequest(requester.id)}
                      disabled={loadingManagerRef.current.isLoading(
                        `decline-request-${requester.id}`
                      )}
                    >
                      {loadingManagerRef.current.isLoading(
                        `decline-request-${requester.id}`
                      )
                        ? "..."
                        : "Decline"}
                    </ButtonOutlineSmall>
                  </div>
                </SearchResultCard>
              ))}
            </FriendsContainer>
          </div>
        )}

        {/* Outgoing Friend Requests */}
        {outgoingRequests.length > 0 && !searchQuery && (
          <div className="mt-30">
            <P2 bold color={White08} style={{ padding: "20px 20px 10px 20px" }}>
              Outgoing Requests
            </P2>
            <FriendsContainer>
              {outgoingRequests.map((recipient) => (
                <SearchResultCard key={recipient.id}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {recipient.photoURL ? (
                      <ProPicSm src={recipient.photoURL} />
                    ) : (
                      <SecPicSm>
                        <H1>
                          {recipient.displayName
                            ? recipient.displayName[0]
                            : "?"}
                        </H1>
                      </SecPicSm>
                    )}
                    <FriendInfo>
                      <P1 color={White}>
                        {recipient.displayName || "No name"}
                      </P1>
                      <P3 color={White08}>
                        @{recipient.username.toLowerCase()}
                      </P3>
                    </FriendInfo>
                  </div>
                  <ButtonOutlineSmall
                    $background={White08}
                    onClick={() => cancelFriendRequest(recipient.id)}
                    disabled={loadingManagerRef.current.isLoading(
                      `cancel-request-${recipient.id}`
                    )}
                  >
                    {loadingManagerRef.current.isLoading(
                      `cancel-request-${recipient.id}`
                    )
                      ? "Canceling..."
                      : "Cancel"}
                  </ButtonOutlineSmall>
                </SearchResultCard>
              ))}
            </FriendsContainer>
          </div>
        )}

        {/* Quick Add Section */}
        {quickAddUsers.length > 0 && !searchQuery && (
          <QuickAddContainer className="mt-20">
            <P2 bold color={White08} style={{ padding: "20px 20px 10px 20px" }}>
              Mutual Quick Add
            </P2>
            <QuickAddScroll>
              {quickAddUsers.map((user) => (
                <QuickAddCard key={user.id}>
                  {user.photoURL ? (
                    <ProPicSm
                      src={user.photoURL}
                      style={{ margin: "0 auto" }}
                    />
                  ) : (
                    <SecPicSm style={{ margin: "0 auto" }}>
                      <H1>
                        {user.displayName
                          ? user.displayName[0].toUpperCase()
                          : "?"}
                      </H1>
                    </SecPicSm>
                  )}
                  <P1 color={White} style={{ margin: "8px 0 2px 0" }}>
                    {user.displayName
                      ? user.displayName.split(" ")[0]
                      : "No name"}
                  </P1>
                  <P3 color={White08}>@{user.username.toLowerCase()}</P3>
                  <ButtonOutlineSmall
                    className="mt-10"
                    $background={White08}
                    disabled={loadingManagerRef.current.isLoading(
                      `add-friend-${user.id}`
                    )}
                    onClick={() => sendFriendRequest(user.id)}
                  >
                    {loadingManagerRef.current.isLoading(
                      `add-friend-${user.id}`
                    )
                      ? "Adding..."
                      : "Add"}
                  </ButtonOutlineSmall>
                </QuickAddCard>
              ))}
            </QuickAddScroll>
          </QuickAddContainer>
        )}

        {/* Friends List */}
        {friends.length > 0 ? (
          <div className="mt-20">
            <P2 bold color={White08} style={{ padding: "20px 20px 10px 20px" }}>
              Your Friends
            </P2>
            <SwipeableList threshold={0.5} type={Type.IOS}>
              {friends.map((friend) => (
                <SwipeableListItem
                  key={friend.id}
                  trailingActions={trailingActions(friend.id)}
                >
                  <Link
                    to={`/${friend.username.toLowerCase()}`}
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    <FriendListItem>
                      <FriendListInfo>
                        {friend.photoURL ? (
                          <ProPicSm src={friend.photoURL} />
                        ) : (
                          <SecPicSm>
                            <H1>
                              {friend.displayName
                                ? friend.displayName[0].toUpperCase()
                                : "?"}
                            </H1>
                          </SecPicSm>
                        )}
                        <FriendDetailsColumn>
                          <FriendNameRow>
                            <P1 color={White}>
                              {friend.displayName
                                ? friend.displayName.split(" ")[0]
                                : "No name"}
                            </P1>
                            <P3 color={White08} style={{ fontWeight: 600 }}>
                              @{friend.username.toLowerCase()}
                            </P3>
                          </FriendNameRow>
                          {renderBig3Traits(friend.id)}
                        </FriendDetailsColumn>
                      </FriendListInfo>
                      <FriendArrow />
                    </FriendListItem>
                  </Link>
                </SwipeableListItem>
              ))}
            </SwipeableList>
          </div>
        ) : (
          !loadingManagerRef.current.isLoading("friends-load") && (
            <InfoBox>
              <H1 color={White08}>No friends yet!</H1>
              <P2 color={White08} className="mt-15">
                Use the search bar above to find friends by their username.
              </P2>
            </InfoBox>
          )
        )}
      </Col>
    </Layout>
  );
};

export default Friends;
